const submitting = new WeakSet<EventTarget>();

interface SubmitLike {
  currentTarget: EventTarget;
  preventDefault(): void;
}

/**
 * Single-flight guard for `<form onSubmit>`. Wrap the handler and return its promise
 * (`onSubmit={guardSubmit(async (e) => { e.preventDefault(); await save(); })}`): while that
 * promise is pending, a second Enter press or submit click on the same form is swallowed, so a
 * slow server cannot receive the submission twice. Handlers that return nothing are not locked.
 */
export function guardSubmit<E extends SubmitLike>(handler: (event: E) => unknown): (event: E) => void {
  return (event) => {
    const form = event.currentTarget;
    if (submitting.has(form)) {
      event.preventDefault();
      return;
    }
    const result = handler(event);
    if (result && typeof (result as PromiseLike<unknown>).then === "function") {
      submitting.add(form);
      const release = () => {
        submitting.delete(form);
      };
      (result as PromiseLike<unknown>).then(release, release);
    }
  };
}
